/**
 * Profile – Settings tab
 * HTML template and setup functions for the Settings tab
 */

import { authApi, notificationsApi, invitesApi } from '../../../api';
import { store } from '../../../state';
import { navigate } from '../../../router';
import { disconnect as disconnectSocket } from '../../../socket';
import { setTheme } from '../../../theme';
import { showToast, showApiError } from '../../../main';
import { isIOSNative, isNativePlatform } from '../../../config';
import {
  TOAST_ACCOUNT_DELETED,
  TOAST_LINK_COPIED,
  TOAST_ENTER_HANDLE,
  API_ERR_DELETE_ACCOUNT,
  TOAST_INVITE_NO_EMAILS,
  TOAST_INVITE_TOO_MANY,
  API_ERR_SEND_INVITES,
  toastInvitesSummary,
} from '../../../utils/app-strings';

export interface SettingsTabData {
  email: string;
  emailVerified: boolean;
  phoneNumber: string;
  handle: string;
  discoverable: boolean;
  liveTypingEnabled: boolean;
  showMatureContent: boolean;
  showExplicitContent: boolean;
  units: string;
  theme: string;
  isActive: boolean;
  isAdmin: boolean;
}

export function renderSettingsTab(data: SettingsTabData): string {
  return `
      <div class="view-scroll seg-panel${data.isActive ? ' active' : ''}" id="tab-settings">
        <div id="account-form" class="profile-edit-form" data-lpignore="true" data-1p-ignore autocomplete="off">
          <h3 class="mb-3">Account</h3>

          <div class="input-group">
            <label for="email" class="group-label">Email</label>
            <div class="input-with-indicator">
              <input
                type="email"
                id="email"
                name="email"
                class="input"
                value="${data.email}"
                data-field="user"
                autocomplete="email"
                inputmode="email"
              />
              <span class="save-indicator" id="indicator-email">Saved <i class="fas fa-check"></i></span>
            </div>
            <div id="email-verify-status" class="age-display">${data.emailVerified ? '<i class="fas fa-check-circle" style="color: var(--success);"></i> Verified' : '<i class="fas fa-times-circle" style="color: var(--text-muted);"></i> Not verified'}</div>
          </div>

          <div class="input-group">
            <label for="phone" class="group-label">Phone</label>
            <div class="input-with-indicator">
              <input
                type="tel"
                id="phone"
                class="input"
                value="${data.phoneNumber}"
                data-field="user"
              />
              <span class="save-indicator" id="indicator-phone">Saved <i class="fas fa-check"></i></span>
            </div>
          </div>

          <div class="input-group">
            <label for="handle" class="group-label">Profile Link</label>
            <div class="input-with-indicator">
              <input
                type="text"
                id="handle"
                class="input"
                value="${data.handle}"
                data-field="handle"
                minlength="3"
                maxlength="20"
                pattern="[a-zA-Z0-9]+"
                placeholder="YourHandle"
              />
              <span class="save-indicator" id="indicator-handle">Saved <i class="fas fa-check"></i></span>
            </div>
            <div class="handle-link-row" id="copy-link-area">
              <span class="handle-link-preview" id="handle-link-preview">
                ${window.location.origin}/#/u/${data.handle}
              </span>
              <span class="handle-copy-label" id="copy-link-btn">
                <i class="fas fa-copy"></i> Copy Link
              </span>
            </div>
            <small class="field-hint">3-20 characters, letters and numbers only</small>
          </div>
        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />

        <div id="visibility-form" class="profile-edit-form" data-lpignore="true" data-1p-ignore autocomplete="off">
          <h3 class="mb-3">Settings</h3>

          <div class="input-group">
            <div class="label-with-indicator">
              <label class="checkbox-item">
                <input type="checkbox" id="show_mature_content"
                  ${data.showMatureContent ? 'checked' : ''}
                  data-field="preferences" />
                Show Mature Content
              </label>
              <span class="save-indicator" id="indicator-show_mature_content">Saved <i class="fas fa-check"></i></span>
            </div>
            <small class="field-hint">See questions about sex, intimacy, and relationships</small>
          </div>

          <div class="input-group">
            <div class="label-with-indicator">
              <label class="checkbox-item">
                <input type="checkbox" id="show_explicit_content"
                  ${data.showExplicitContent ? 'checked' : ''}
                  data-field="preferences" />
                Show Explicit Content
              </label>
              <span class="save-indicator" id="indicator-show_explicit_content">Saved <i class="fas fa-check"></i></span>
            </div>
            <small class="field-hint">See graphic or sexually explicit questions</small>
          </div>

          <div class="input-group">
            <div class="label-with-indicator">
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  id="discoverable"
                  ${data.discoverable ? 'checked' : ''}
                  data-field="visibility"
                />
                Profile Active
              </label>
              <span class="save-indicator" id="indicator-discoverable">Saved <i class="fas fa-check"></i></span>
            </div>
            <small class="field-hint">When paused, you won't appear in browse</small>
          </div>

          <div class="input-group">
            <div class="label-with-indicator">
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  id="live_typing_enabled"
                  ${data.liveTypingEnabled ? 'checked' : ''}
                  data-field="preferences"
                />
                Live Typing
              </label>
              <span class="save-indicator" id="indicator-live_typing">Saved <i class="fas fa-check"></i></span>
            </div>
            <small class="field-hint">Others can see you type in real-time</small>
          </div>

          <div class="preference-section">
            <div class="preference-row">
              <span class="group-label">Units</span>
              <span class="save-indicator" id="indicator-units">Saved <i class="fas fa-check"></i></span>
            </div>
            <div class="radio-group units-radio-group" id="units_preference">
              <label class="radio-item">
                <input
                  type="radio"
                  name="units_preference"
                  value="imperial"
                  ${data.units === 'imperial' ? 'checked' : ''}
                  data-field="filters_json"
                  data-json-path="app.units"
                />
                <span class="radio-circle"></span>
                Imperial (mi, ft, lb)
              </label>
              <label class="radio-item">
                <input
                  type="radio"
                  name="units_preference"
                  value="metric"
                  ${data.units === 'metric' ? 'checked' : ''}
                  data-field="filters_json"
                  data-json-path="app.units"
                />
                <span class="radio-circle"></span>
                Metric (km, cm, kg)
              </label>
            </div>
          </div>

          <div class="preference-section">
            <div class="preference-row">
              <span class="group-label">Theme</span>
              <span class="save-indicator" id="indicator-theme">Saved <i class="fas fa-check"></i></span>
            </div>
            <div class="radio-group units-radio-group" id="theme_preference">
              <label class="radio-item">
                <input type="radio" name="theme_preference" value="system" ${data.theme === 'system' ? 'checked' : ''} />
                <span class="radio-circle"></span>
                System
              </label>
              <label class="radio-item">
                <input type="radio" name="theme_preference" value="light" ${data.theme === 'light' ? 'checked' : ''} />
                <span class="radio-circle"></span>
                Light
              </label>
              <label class="radio-item">
                <input type="radio" name="theme_preference" value="dark" ${data.theme === 'dark' ? 'checked' : ''} />
                <span class="radio-circle"></span>
                Dark
              </label>
            </div>
          </div>

        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />

        <div id="notifications-form" class="profile-edit-form" data-lpignore="true" data-1p-ignore autocomplete="off">
          <h3 class="mb-3">Notifications</h3>
          <div id="notif-prefs-container">
            <p class="text-muted" style="font-size: var(--text-small);">Loading...</p>
          </div>
        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />

        ${data.isAdmin ? `
        <div class="settings-actions">
          <h3 class="mb-3" style="color: var(--text-muted); font-size: var(--text-small); text-transform: uppercase; letter-spacing: 0.5px;">Admin Only</h3>
          <button class="btn btn-secondary" id="ai-editor-btn"><i class="fas fa-brain"></i> AI Editor</button>
        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />
        ` : ''}

        <div class="settings-actions">
          ${isIOSNative() ? '' : `
          <a href="https://buy.stripe.com/4gM14o74S273eVT5pk4c800" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
            <i class="fas fa-circle-check"></i>
            Tip $5
          </a>
          `}

          <button id="invite-friends-btn" class="btn btn-secondary">
            <i class="fas fa-user-plus"></i>
            Invite Friends
          </button>

          <div id="invite-form" style="display: none; margin-top: 0.5rem;">
            <p style="color: var(--text-secondary); font-size: var(--text-small); margin: 0 0 0.5rem;">
              Invite friends by email. When they sign up, you'll be automatically matched!
            </p>
            <textarea id="invite-emails" class="input" rows="3"
              placeholder="friend@email.com, another@email.com"
              style="width: 100%; resize: vertical;"></textarea>
            <p class="text-caption" style="margin: 0.25rem 0 0;">Separate with commas, spaces, or new lines (max 10)</p>
            <div class="flex gap-2" style="margin-top: 0.75rem;">
              <button class="btn btn-secondary" id="invite-cancel-btn">Cancel</button>
              <button class="btn btn-primary" id="invite-send-btn">Send Invites</button>
            </div>
          </div>

          <button class="btn btn-secondary" id="setup-wizard-btn">
            <i class="fas fa-wand-magic-sparkles"></i>
            Setup Wizard
          </button>

          <button id="logout-btn" class="btn btn-secondary">
            <i class="fas fa-sign-out-alt"></i>
            Log Out
          </button>
        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />

        <div class="settings-legal-links">
          <div>
            <a href="#/privacy">Privacy Policy</a>
            <span class="settings-legal-divider">|</span>
            <a href="#/terms">Terms of Service</a>
          </div>
          <div>
            <a href="#/contact">Contact</a>
            <span class="settings-legal-divider">|</span>
            <a href="#/welcome">Home</a>
          </div>
        </div>

        <hr style="border-color: var(--border); margin: 1.5rem 0;" />

        <div class="settings-actions">
          <button id="delete-account-btn" class="btn btn-danger-outline">
            <i class="fas fa-trash-alt"></i>
            Delete Account
          </button>
        </div>
      </div>
  `;
}

export function renderDeleteModal(): string {
  return `
    <div id="delete-account-modal" class="modal" style="display: none;">
      <div class="modal-backdrop"></div>
      <div class="modal-content">
        <h3>Delete Your Account?</h3>
        <p class="modal-warning">This action is <strong>permanent</strong> and cannot be undone.</p>
        <p>Deleting your account will remove:</p>
        <ul class="modal-list">
          <li>Your profile and all photos</li>
          <li>All messages and chats</li>
          <li>All matches and interactions</li>
          <li>Your preferences and settings</li>
        </ul>
        <p style="margin-top: 1rem;">Type <strong>DELETE</strong> to confirm:</p>
        <input type="text" id="delete-confirm-input" class="input" placeholder="Type DELETE" autocomplete="off" />
        <div class="modal-actions">
          <button id="delete-cancel-btn" class="btn btn-secondary">Cancel</button>
          <button id="delete-confirm-btn" class="btn btn-danger" disabled>Delete Account</button>
        </div>
      </div>
    </div>
  `;
}

export function setupLogout(): void {
  document.getElementById('ai-editor-btn')?.addEventListener('click', () => navigate('/knowledge'));
  document.getElementById('setup-wizard-btn')?.addEventListener('click', () => navigate('/onboarding'));

  document.getElementById('logout-btn')!.addEventListener('click', async () => {
    try {
      // Deactivate push token before logging out
      import('../../../push').then(m => m.deactivatePush()).catch(() => {});
      await authApi.logout();
    } catch (err) {
      // Ignore errors on logout
    }

    disconnectSocket();
    store.clearTokens();
    navigate('/welcome');
  });
}

export function setupDeleteAccount(): void {
  const deleteBtn = document.getElementById('delete-account-btn')!;
  const modal = document.getElementById('delete-account-modal')!;
  const backdrop = modal.querySelector('.modal-backdrop')!;
  const confirmInput = document.getElementById('delete-confirm-input') as HTMLInputElement;
  const cancelBtn = document.getElementById('delete-cancel-btn')!;
  const confirmBtn = document.getElementById('delete-confirm-btn') as HTMLButtonElement;

  const openModal = () => {
    modal.style.display = 'flex';
    confirmInput.value = '';
    confirmBtn.disabled = true;
    confirmInput.focus();
  };

  const closeModal = () => {
    modal.style.display = 'none';
    confirmInput.value = '';
    confirmBtn.disabled = true;
  };

  deleteBtn.addEventListener('click', openModal);
  backdrop.addEventListener('click', closeModal);
  cancelBtn.addEventListener('click', closeModal);

  // Enable confirm button only when "DELETE" is typed
  confirmInput.addEventListener('input', () => {
    confirmBtn.disabled = confirmInput.value.toUpperCase() !== 'DELETE';
  });

  // Handle account deletion
  confirmBtn.addEventListener('click', async () => {
    if (confirmInput.value.toUpperCase() !== 'DELETE') return;

    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Deleting...';

    try {
      import('../../../push').then(m => m.deactivatePush()).catch(() => {});
      await authApi.deleteAccount();
      disconnectSocket();
      store.clearTokens();
      showToast(TOAST_ACCOUNT_DELETED, 'success');
      navigate('/welcome');
    } catch (err: any) {
      showApiError(err, API_ERR_DELETE_ACCOUNT);
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Delete Account';
    }
  });

  // Close modal on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.style.display === 'flex') {
      closeModal();
    }
  });
}

export function setupHandle(): void {
  const handleInput = document.getElementById('handle') as HTMLInputElement;
  const linkPreview = document.getElementById('handle-link-preview');
  const copyBtn = document.getElementById('copy-link-btn');

  if (!handleInput || !linkPreview || !copyBtn) return;

  // Update link preview when handle changes
  handleInput.addEventListener('input', () => {
    const handle = handleInput.value.trim();
    if (handle) {
      linkPreview.textContent = `${window.location.origin}/#/u/${handle}`;
    } else {
      linkPreview.textContent = `${window.location.origin}/#/u/`;
    }
  });

  // Copy link handler - whole box is clickable
  const copyArea = document.getElementById('copy-link-area');
  (copyArea || copyBtn).addEventListener('click', async () => {
    const handle = handleInput.value.trim();
    if (!handle) {
      showToast(TOAST_ENTER_HANDLE, 'error');
      return;
    }
    const url = `${window.location.origin}/#/u/${handle}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(TOAST_LINK_COPIED, 'success');
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast(TOAST_LINK_COPIED, 'success');
    }
  });
}

// ============================================================================
// Notification Preferences
// ============================================================================

const NOTIF_CATEGORIES = [
  { key: 'matches', label: 'New Matches', hint: 'When you match with someone' },
  { key: 'messages', label: 'Messages', hint: 'When you receive a new message' },
  { key: 'likes', label: 'New Likes', hint: 'When someone is interested in you' },
  { key: 'qa_activity', label: 'Q&A Activity', hint: 'Answers, comparisons, and compatibility sparks' },
  { key: 'nudges', label: 'Reminders', hint: 'Gentle reminders about quiet matches' },
  { key: 'typing_alert', label: 'Typing Alerts', hint: 'When someone is writing you a long message' },
] as const;

const COOLDOWN_OPTIONS = [
  { value: 0, label: 'Instant' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 1440, label: 'Daily' },
] as const;

function renderNotifPrefsHtml(prefs: Record<string, any>): string {
  const categories = NOTIF_CATEGORIES.map(cat => {
    const catPref = prefs[cat.key] || { enabled: true, cooldown_min: 60 };
    const cooldownOptions = COOLDOWN_OPTIONS.map(opt =>
      `<option value="${opt.value}" ${catPref.cooldown_min === opt.value ? 'selected' : ''}>${opt.label}</option>`
    ).join('');

    return `
      <div class="notif-category-row">
        <div class="notif-category-info">
          <label class="checkbox-item">
            <input type="checkbox" data-notif-cat="${cat.key}" ${catPref.enabled ? 'checked' : ''} />
            ${cat.label}
          </label>
          <small class="field-hint">${cat.hint}</small>
        </div>
        <select class="input notif-cooldown-select" data-notif-cooldown="${cat.key}" ${!catPref.enabled ? 'disabled' : ''}>
          ${cooldownOptions}
        </select>
      </div>
    `;
  }).join('');

  const quietStart = prefs.quiet_start || '22:00';
  const quietEnd = prefs.quiet_end || '08:00';
  const emailDigest = prefs.email_digest || 'weekly';

  return `
    ${categories}

    <div class="notif-section-divider"></div>

    <div class="input-group">
      <label class="group-label">Quiet Hours</label>
      <div class="notif-quiet-hours">
        <input type="time" id="notif-quiet-start" class="input" value="${quietStart}" />
        <span>to</span>
        <input type="time" id="notif-quiet-end" class="input" value="${quietEnd}" />
      </div>
      <small class="field-hint">No push notifications during these hours</small>
    </div>

    <div class="input-group">
      <label class="group-label">Weekly Email Digest</label>
      <select id="notif-email-digest" class="input">
        <option value="weekly" ${emailDigest === 'weekly' ? 'selected' : ''}>Enabled</option>
        <option value="none" ${emailDigest === 'none' ? 'selected' : ''}>Disabled</option>
      </select>
      <small class="field-hint">Summary of your week's activity</small>
    </div>

    <span class="save-indicator notif-save-indicator" id="indicator-notif">Saved <i class="fas fa-check"></i></span>
  `;
}

export async function setupNotificationPrefs(): Promise<void> {
  const container = document.getElementById('notif-prefs-container');
  if (!container) return;

  try {
    const res = await notificationsApi.getPreferences();
    const prefs = res.data;
    container.innerHTML = renderNotifPrefsHtml(prefs);

    const indicator = document.getElementById('indicator-notif');
    let saveTimeout: ReturnType<typeof setTimeout> | null = null;

    const savePrefs = async () => {
      const updated: Record<string, any> = {};

      for (const cat of NOTIF_CATEGORIES) {
        const checkbox = container.querySelector(`[data-notif-cat="${cat.key}"]`) as HTMLInputElement;
        const select = container.querySelector(`[data-notif-cooldown="${cat.key}"]`) as HTMLSelectElement;
        if (checkbox && select) {
          updated[cat.key] = {
            enabled: checkbox.checked,
            cooldown_min: parseInt(select.value, 10),
          };
        }
      }

      const quietStart = (document.getElementById('notif-quiet-start') as HTMLInputElement)?.value;
      const quietEnd = (document.getElementById('notif-quiet-end') as HTMLInputElement)?.value;
      const emailDigest = (document.getElementById('notif-email-digest') as HTMLSelectElement)?.value;

      if (quietStart) updated.quiet_start = quietStart;
      if (quietEnd) updated.quiet_end = quietEnd;
      if (emailDigest) updated.email_digest = emailDigest;

      try {
        await notificationsApi.updatePreferences(updated);
        if (indicator) {
          indicator.classList.add('visible');
          if (saveTimeout) clearTimeout(saveTimeout);
          saveTimeout = setTimeout(() => indicator.classList.remove('visible'), 1500);
        }
      } catch (err) {
        console.error('[Notif Prefs] Save failed:', err);
      }
    };

    // Enable/disable cooldown select when category toggled
    container.querySelectorAll('[data-notif-cat]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const el = checkbox as HTMLInputElement;
        const key = el.dataset.notifCat!;
        const select = container.querySelector(`[data-notif-cooldown="${key}"]`) as HTMLSelectElement;
        if (select) select.disabled = !el.checked;
        savePrefs();
      });
    });

    // Save on cooldown change
    container.querySelectorAll('[data-notif-cooldown]').forEach(select => {
      select.addEventListener('change', savePrefs);
    });

    // Save on quiet hours change
    document.getElementById('notif-quiet-start')?.addEventListener('change', savePrefs);
    document.getElementById('notif-quiet-end')?.addEventListener('change', savePrefs);
    document.getElementById('notif-email-digest')?.addEventListener('change', savePrefs);
  } catch (err) {
    container.innerHTML = '<p class="text-muted" style="font-size: var(--text-small);">Could not load notification settings.</p>';
  }
}

export function setupThemeToggle(): void {
  const themeGroup = document.getElementById('theme_preference');
  if (!themeGroup) return;

  const indicator = document.getElementById('indicator-theme');

  themeGroup.querySelectorAll('input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', () => {
      const radioEl = radio as HTMLInputElement;
      if (radioEl.checked) {
        setTheme(radioEl.value as 'system' | 'light' | 'dark');

        // Show saved indicator briefly
        if (indicator) {
          indicator.classList.add('visible');
          setTimeout(() => indicator.classList.remove('visible'), 1500);
        }
      }
    });
  });
}

function parseEmails(raw: string): string[] {
  return [...new Set(
    raw
      .split(/[,\s\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.length > 0 && e.includes('@'))
  )];
}

export function setupInviteFriends(): void {
  const toggleBtn = document.getElementById('invite-friends-btn');
  const form = document.getElementById('invite-form');
  const sendBtn = document.getElementById('invite-send-btn');
  const cancelBtn = document.getElementById('invite-cancel-btn');
  if (!toggleBtn || !form) return;

  toggleBtn.addEventListener('click', () => {
    const isVisible = form.style.display !== 'none';
    form.style.display = isVisible ? 'none' : 'block';
    if (!isVisible) {
      (document.getElementById('invite-emails') as HTMLTextAreaElement)?.focus();
    }
  });

  cancelBtn?.addEventListener('click', () => {
    form.style.display = 'none';
    const textarea = document.getElementById('invite-emails') as HTMLTextAreaElement;
    if (textarea) textarea.value = '';
  });

  sendBtn?.addEventListener('click', async () => {
    const textarea = document.getElementById('invite-emails') as HTMLTextAreaElement;
    if (!textarea) return;

    const emails = parseEmails(textarea.value);

    if (emails.length === 0) {
      showToast(TOAST_INVITE_NO_EMAILS);
      return;
    }
    if (emails.length > 10) {
      showToast(TOAST_INVITE_TOO_MANY);
      return;
    }

    sendBtn.setAttribute('disabled', 'true');
    sendBtn.textContent = 'Sending...';

    try {
      const response = await invitesApi.send(emails);
      const { sent, matched } = response.data;
      showToast(toastInvitesSummary(sent, matched));
      textarea.value = '';
      form.style.display = 'none';
    } catch (err: any) {
      showApiError(err, API_ERR_SEND_INVITES);
    } finally {
      sendBtn.removeAttribute('disabled');
      sendBtn.textContent = 'Send Invites';
    }
  });
}
